import { FC, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { login, register } from "../api/auth/auth.api";
import { LOGIN, PLATFORM } from "../common/constants/routes.constants";

interface IAuthContextData {
  login: (email: string, password: string) => Promise<string | void>;
  register: (email: string, password: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext({} as IAuthContextData);

const LOCAL_STORAGE_KEY__ACCESS_TOKEN = "APP_ACCESS_TOKEN";

interface IAuthProps {
  children: ReactNode;
}

export const AuthProvider: FC<IAuthProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string>();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);

    if (token) {
      setAccessToken(JSON.parse(token));
    } else {
      setAccessToken(undefined);
    }
  }, [accessToken]);

  const handleLogin = useCallback(async (email: string, password: string) => {
    const result = await login({ email, password });
    if (result instanceof Error) {
      return result.message;
    } else {
      localStorage.setItem(
        LOCAL_STORAGE_KEY__ACCESS_TOKEN,
        JSON.stringify(result.response)
      );
      setAccessToken(result.response);
      navigate(PLATFORM);
    }
  }, []);

  const handleRegister = useCallback(
    async (email: string, password: string) => {
      const result = await register({ email, password });
      if (result instanceof Error) {
        return result.message;
      } else {
        await handleLogin(email, password);
      }
    },
    []
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY__ACCESS_TOKEN);
    setAccessToken(undefined);
    navigate(LOGIN);
  }, []);

  const isAuthenticated = useMemo(() => !!accessToken, [accessToken]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login: handleLogin,
        register: handleRegister,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
